import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { makeApi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";

export default function MySubjects() {
  const { me, setMe } = useOutletContext();
  const { getToken } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [families, setFamilies] = useState(["UGC_NET", "CSIR_NET"]);
  const [tab, setTab] = useState("UGC_NET");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!me) return;
    setSelected(new Set(me.selected_subjects || []));
    const fams = me.selected_families?.length ? me.selected_families : ["UGC_NET", "CSIR_NET"];
    setFamilies(fams);
    setTab(fams[0]);
    const api = makeApi(getToken);
    api.get("/subjects").then((r) => setSubjects(r.data)).catch(() => toast.error("Failed to load subjects"));
  }, [me, getToken]);

  const filtered = useMemo(() => {
    const list = subjects.filter((s) => s.family === tab);
    if (!q.trim()) return list;
    const t = q.toLowerCase();
    return list.filter((s) => s.name.toLowerCase().includes(t) || s.code.includes(t));
  }, [subjects, tab, q]);

  const toggle = (sid) => {
    setSelected((cur) => {
      const n = new Set(cur);
      if (n.has(sid)) n.delete(sid);
      else n.add(sid);
      return n;
    });
  };

  const toggleFamily = (f) => {
    setFamilies((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));
  };

  const save = async () => {
    if (!families.length) return toast.error("Pick at least one exam family");
    if (!selected.size) return toast.error("Select at least one subject");
    setSaving(true);
    try {
      const api = makeApi(getToken);
      const r = await api.post("/me/onboarding", {
        families,
        subjects: Array.from(selected),
      });
      setMe((prev) => ({ ...prev, selected_families: r.data.families, selected_subjects: r.data.subjects, onboarded: true }));
      toast.success("Subjects updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="my-subjects-page">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">My subjects</h1>
        <p className="text-slate-600 mt-2 text-sm">Add or remove subjects anytime — your past progress is preserved.</p>
      </div>

      <Card className="p-5 border-slate-200">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">Exam families</p>
        <div className="flex flex-wrap gap-2">
          {["UGC_NET", "CSIR_NET"].map((f) => (
            <button
              key={f}
              onClick={() => toggleFamily(f)}
              data-testid={`fam-toggle-${f}`}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                families.includes(f)
                  ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              {f.replace("_", " ")}
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-5 border-slate-200">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100">
            {families.includes("UGC_NET") && <TabsTrigger value="UGC_NET">UGC NET</TabsTrigger>}
            {families.includes("CSIR_NET") && <TabsTrigger value="CSIR_NET">CSIR NET</TabsTrigger>}
          </TabsList>
          {families.map((fam) => (
            <TabsContent key={fam} value={fam} className="mt-5">
              <div className="relative mb-4">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by code or name"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9"
                  data-testid="my-subjects-search"
                />
              </div>
              <div className="max-h-[480px] overflow-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {filtered.map((s) => {
                  const sid = `${s.family}:${s.code}`;
                  const checked = selected.has(sid);
                  return (
                    <label
                      key={sid}
                      htmlFor={`msb-${sid}`}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 ${checked ? "bg-blue-50/60" : ""}`}
                      data-testid={`my-subject-${s.family}-${s.code}`}
                    >
                      <Checkbox id={`msb-${sid}`} checked={checked} onCheckedChange={() => toggle(sid)} />
                      <span className="font-mono text-xs text-slate-500 w-10">{s.code}</span>
                      <span className="text-sm text-slate-900 flex-1">{s.name}</span>
                      <Badge variant="outline" className="border-slate-200 text-xs text-slate-500">
                        {s.verification_status}
                      </Badge>
                    </label>
                  );
                })}
                {!filtered.length && <div className="px-4 py-10 text-center text-sm text-slate-500">No subjects match.</div>}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        <div className="flex items-center justify-between mt-6">
          <span className="text-sm text-slate-600" data-testid="my-subjects-count">{selected.size} selected</span>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="my-subjects-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
